import { Controller } from "@hotwired/stimulus"
import * as UpChunk from '@mux/upchunk'

export default class extends Controller {
  static targets = ["Form", "FileInput", "UploadingContainer", "FilenamesList", "ProgressBar", "ErrorContainer", "ErrorText", "VideoUploadingContainer", "VideoStatusText", "VideoProgressBar"]
  static values = {
    preUploadAction: String,
    postUploadAction: String,
    appendListItemAction: String,
    appendListItemId: String,
    originDomain: String,
    zipUploadError: String
  }

  connect() {
    this.conversionIntervals = {}
    this.allFilenames = []
    this.droppedFiles = false
    const div = document.createElement('div');
    this.isAdvancedUpload = (('draggable' in div) || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window

    this.initializeEventListeners()
  }

  initializeEventListeners() {
    // submit form on file input change (drop)
    this.FileInputTarget.addEventListener('change', (e) => {
      this.onSubmit(e)
    })

    if (this.isAdvancedUpload) {
      window.addEventListener('dragover', this.stopPropagation)
      window.addEventListener('drop', this.stopPropagation)

      this.FormTarget.classList.add("has-advanced-upload")

      this.FormTarget.addEventListener('drag', this.stopPropagation)
      this.FormTarget.addEventListener('dragstart', this.stopPropagation)
      this.FormTarget.addEventListener('dragover', (e) => {
        this.stopPropagation(e)
        this.FormTarget.classList.add('is-dragover')
      })
      this.FormTarget.addEventListener('dragenter', (e) => {
        this.stopPropagation(e)
        this.FormTarget.classList.add('is-dragover')
      })
      this.FormTarget.addEventListener('dragleave', (e) => {
        this.stopPropagation(e)
        this.FormTarget.classList.remove('is-dragover')
      })
      this.FormTarget.addEventListener('dragend', (e) => {
        this.stopPropagation(e)
        this.FormTarget.classList.remove('is-dragover')
      })
      this.FormTarget.addEventListener('drop', (e) => {
        this.stopPropagation(e)
        this.FormTarget.classList.remove('is-dragover')
        this.droppedFiles = e.dataTransfer.files
        this.onSubmit(e)
      })
    }

    this.FormTarget.addEventListener('submit', this.onSubmit)
  }

  stopPropagation(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  isVideoFile(file) {
    const videoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/webm']
    return videoTypes.includes(file.type)
  }

  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  getResourceInfo() {
    const action = this.FormTarget.getAttribute('action')
    const match = action.match(/\?(\w+)_id=(\d+)/)
    if (match) {
      return {
        type: match[1],
        id: match[2]
      }
    }
    return null
  }

  callControllerMethod(controllerAndMethod, payload = null, formElement = null, id = null) {
    const parts = controllerAndMethod.split('->')
    const controller = parts[0]
    const method = parts[1]
    let controllerElement
    if (id) {
      const controllerElems = document.querySelectorAll(`[data-controller="${controller}"]`)
      controllerElement = Array.from(controllerElems).find((elem) => elem.id == id)
    } else {
      controllerElement = document.querySelectorAll(`[data-controller="${controller}"]`)[0]
    }
    const controllerInstance = this.application.getControllerForElementAndIdentifier(controllerElement, controller)
    if (controllerInstance) {
      if (payload && formElement) {
        controllerInstance[method](payload, formElement)
      } else if (payload) {
        controllerInstance[method](payload)
      } else if (formElement) {
        controllerInstance[method](formElement)
      } else {
        controllerInstance[method]()
      }
    }
  }

  onSubmit(e) {
    e.preventDefault()

    if (this.FormTarget.classList.contains('is-uploading')) return false;

    this.FormTarget.classList.add('is-uploading')
    this.FormTarget.classList.remove('is-error')
    this.ErrorContainerTarget.classList.add('hidden')

    if (this.isAdvancedUpload) {
      this.uploadAdvanced(e) // ajax for modern browsers
    } else {
      this.uploadLegacy(e) // ajax for legacy browsers
    }
  }

  async uploadAdvanced(e) {
    e.preventDefault()

    // Collect all files
    let allFiles = []
    if (this.droppedFiles) {
      allFiles = Array.from(this.droppedFiles)
    } else if (this.FileInputTarget.files && this.FileInputTarget.files.length > 0) {
      allFiles = Array.from(this.FileInputTarget.files)
    }

    console.log('All files:', allFiles)

    // Separate video files from regular files
    const videoFiles = allFiles.filter(file => this.isVideoFile(file))
    const regularFiles = allFiles.filter(file => !this.isVideoFile(file))

    console.log('Video files:', videoFiles)
    console.log('Regular files:', regularFiles)

    // Track all filenames for display
    this.allFilenames = allFiles.map(file => file.name)

    // Handle video files separately via Mux
    if (videoFiles.length > 0) {
      console.log('Starting video upload...')
      await this.uploadVideosToMux(videoFiles)
    }

    // If there are no regular files, skip the regular upload
    if (regularFiles.length === 0) {
      console.log('No regular files, skipping regular upload')
      return
    }

    // Handle regular files with existing logic.
    // FormData(form) already serializes the file input's current files, so
    // drop them before appending the collected files explicitly — otherwise
    // each upload is sent twice (and a stale input leaks the previous file).
    let formData = new FormData(this.FormTarget)
    formData.delete(this.FileInputTarget.getAttribute('name'))
    regularFiles.forEach((file) => {
      formData.append(this.FileInputTarget.getAttribute('name'), file)
    })

    if (this.preUploadActionValue != "") {
      this.callControllerMethod(this.preUploadActionValue, formData)
    }

    const xhr = new XMLHttpRequest()
    xhr.open(this.FormTarget.getAttribute('method'), this.FormTarget.getAttribute('action'))
    xhr.withCredentials = true
    xhr.setRequestHeader('Access-Control-Allow-Origin', this.originDomainValue)
    xhr.setRequestHeader('Access-Control-Allow-Methods', 'HEAD, GET, POST, PUT, PATCH, DELETE')
    xhr.setRequestHeader('Access-Control-Allow-Headers', 'Accept, Content-Type, Referer, Origin, User-Agent')
    xhr.setRequestHeader('Access-Control-Allow-Credentials', true)

    // upload progress event
    xhr.upload.addEventListener('progress', this.onProgress.bind(this))

    // request finished event
    xhr.addEventListener('load', (e) => {
      if (xhr.status < 400) {
        let data

        try {
          data = JSON.parse(xhr.response)
        } catch {
          this.handleUploadFailure(xhr)
          return
        }

        this.FormTarget.classList.add(data.success == true ? 'is-success' : 'is-error')

        if (this.appendListItemActionValue != "") {
          data.attachments.map((attachment) => {
            if (this.appendListItemIdValue != "") {
              this.callControllerMethod(this.appendListItemActionValue, attachment, null, this.appendListItemIdValue)
            } else {
              this.callControllerMethod(this.appendListItemActionValue, attachment, null)
            }
          })
        }

        if (this.postUploadActionValue != "") {
          this.callControllerMethod(this.postUploadActionValue, data, this.FormTarget)
        }

        this.FormTarget.classList.remove('is-uploading')
        this.allFilenames = []
        this.resetFileInput()
      } else {
        this.handleUploadFailure(xhr)
      }
    })

    xhr.addEventListener('error', () => {
      this.handleUploadFailure(xhr)
    })

    xhr.send(formData)
  }

  handleUploadFailure(xhr) {
    let errors = []

    if (xhr.status == 400) {
      try {
        const data = JSON.parse(xhr.response)
        errors = Array.isArray(data.errors) ? data.errors : []
      } catch {
        errors = []
      }
    }

    const isExpectedZipValidation = this.hasZipUploadErrorValue && errors.length > 0 && errors.every((error) => error === this.zipUploadErrorValue)

    if (errors.length > 0) {
      this.ErrorTextTarget.innerHTML = errors.join(" & ")
    } else {
      this.ErrorTextTarget.innerHTML = "File uploading failed. Please try again and <a href='mailto:support@peoplespeak.net'>contact us</a> if this problem persists."
    }

    this.ErrorContainerTarget.classList.remove("hidden")

    if (window.Sentry && !isExpectedZipValidation) {
      const status = xhr.status ? `${xhr.status} ${xhr.statusText}`.trim() : 'Network error'
      Sentry.captureException('Error Uploading Attachment file: ' + status)
    }

    this.FormTarget.classList.remove('is-uploading')
    this.allFilenames = []
    this.resetFileInput()

    if (this.postUploadActionValue != "") {
      this.callControllerMethod(this.postUploadActionValue)
    }
  }

  // Clear the input and dropped-files state so the next upload doesn't re-send
  // the previous selection (FormData(form) would otherwise pick up stale files).
  resetFileInput() {
    this.FileInputTarget.value = ''
    this.droppedFiles = false
  }

  uploadLegacy() {
    const iframeName = `uploadiframe${new Date().getTime()}`
    const iframeElem = document.createElement("iframe")
    iframeElem.setAttribute('name', iframeName)
    iframeElem.style = "display: none;"

    document.body.appendElement(iframeElem)
    this.FormTarget.setAttribute('target', iframeName);

    iframeElem.addEventListener('load', () => {
      const data = JSON.parse(iframeElem.contents().find('body').text())
      this.FormTarget.classList.remove('is-uploading')
      this.FormTarget.classList.add(data.success == true ? 'is-success' : 'is-error')
      this.FormTarget.removeAttribute('target')
      if (!data.success) this.ErrorContainerTarget.innerHTML = `Oops! ${data.error}`
      iframeElem.remove()
    })
  }

  onProgress(e) {
    if (e.lengthComputable) {
      const max = e.total;
      const current = e.loaded;
      const percentage = (current * 100) / max;
      this.showFilenames(this.allFilenames)

      if (percentage >= 100) {
        // process completed
        this.updateProgressBar(percentage)
      } else {
        // in progress
        this.updateProgressBar(percentage)
      }
    }
  }

  updateProgressBar(percentage) {
    this.ProgressBarTarget.style.width = `${percentage}%`
  }

  showFilenames(filenames) {
    this.FilenamesListTarget.innerHTML = filenames.join(", ")
    this.UploadingContainerTarget.classList.remove("hidden")
  }

  onConversionError() {
    this.ErrorTextTarget.innerHTML = "Something went wrong. Please try refreshing the page and <a href='mailto:support@peoplespeak.net'>contact us</a> if this problem persists."
    this.ErrorContainerTarget.classList.remove("hidden")
    this.element.classList.add('is-error')
  }

  async uploadVideosToMux(videoFiles) {
    for (const videoFile of videoFiles) {
      console.log(`Processing video file: ${videoFile.name}, size: ${videoFile.size} bytes`)

      // Validate file is not empty
      if (videoFile.size === 0) {
        alert(`Video file "${videoFile.name}" is empty (0 bytes). Please select a valid video file.`)
        this.FormTarget.classList.remove('is-uploading')
        continue
      }

      // Validate file size (500MB limit)
      const maxSize = 500 * 1024 * 1024
      if (videoFile.size > maxSize) {
        alert(`Video file "${videoFile.name}" is too large. Maximum size is 500MB.`)
        this.FormTarget.classList.remove('is-uploading')
        continue
      }

      console.log('Requesting Mux upload URL...')
      // Request Mux upload URL
      const uploadUrlData = await this.requestMuxUploadUrl()
      console.log('Mux upload URL response:', uploadUrlData)

      if (!uploadUrlData) {
        alert(`Failed to get upload URL for "${videoFile.name}"`)
        this.FormTarget.classList.remove('is-uploading')
        continue
      }

      console.log('Starting upload to Mux...')
      // Upload to Mux
      this.uploadVideoToMux(videoFile, uploadUrlData)
    }
  }

  async requestMuxUploadUrl() {
    const resourceInfo = this.getResourceInfo()
    if (!resourceInfo) {
      console.error('Could not determine resource type and ID from form action')
      return null
    }

    try {
      const formData = new FormData()
      formData.append('request_mux_upload', 'true')
      formData.append(`${resourceInfo.type}_id`, resourceInfo.id)

      const response = await fetch('/admin/attachments', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': this.getCsrfToken()
        }
      })

      if (!response.ok) throw new Error('Failed to get upload URL')

      const data = await response.json()
      return data.mux_upload_url
    } catch (error) {
      console.error('Error requesting Mux upload URL:', error)
      return null
    }
  }

  uploadVideoToMux(file, uploadUrlData) {
    console.log('uploadVideoToMux called with:', {
      fileName: file.name,
      fileSize: file.size,
      uploadUrl: uploadUrlData.upload_url,
      uploadId: uploadUrlData.upload_id
    })

    // Show video upload container
    if (this.hasVideoUploadingContainerTarget) {
      console.log('Showing video upload container')
      this.VideoUploadingContainerTarget.classList.remove('hidden')
      this.VideoStatusTextTarget.textContent = `Uploading ${file.name}...`
    } else {
      console.warn('VideoUploadingContainer target not found')
    }

    // Check if UpChunk is available
    if (typeof UpChunk === 'undefined') {
      console.error('UpChunk is not defined!')
      alert('Video upload library not loaded. Please refresh the page and try again.')
      return
    }

    console.log('Creating UpChunk upload...')
    try {
      const upload = UpChunk.createUpload({
        endpoint: uploadUrlData.upload_url,
        file: file,
        chunkSize: 256 * 1024 // 256KB chunks as recommended
      })
      console.log('UpChunk upload created:', upload)

      // Add event listeners
      upload.on('error', (error) => {
        console.error('Video upload error:', error)
        if (this.hasVideoStatusTextTarget) {
          this.VideoStatusTextTarget.textContent = `Upload failed for ${file.name}. Please try again.`
        }
        if (this.hasVideoProgressBarTarget) {
          this.VideoProgressBarTarget.style.width = '0%'
        }
        this.FormTarget.classList.remove('is-uploading')
      })

      upload.on('progress', (progress) => {
        const percentage = Math.round(progress.detail * 100)
        console.log(`Upload progress: ${percentage}%`)
        if (this.hasVideoProgressBarTarget) {
          this.VideoProgressBarTarget.style.width = `${percentage}%`
          this.VideoStatusTextTarget.textContent = `Uploading ${file.name}: ${percentage}%`
        }
      })

      upload.on('success', () => {
        console.log('Upload successful!')
        if (this.hasVideoStatusTextTarget) {
          this.VideoStatusTextTarget.textContent = `${file.name} uploaded! Processing...`
        }
        this.createVideoAttachmentRecord(uploadUrlData.upload_id, file)
      })
    } catch (error) {
      console.error('Error creating UpChunk upload:', error)
      alert(`Failed to start upload: ${error.message}`)
      this.FormTarget.classList.remove('is-uploading')
    }
  }

  async createVideoAttachmentRecord(uploadId, file) {
    const resourceInfo = this.getResourceInfo()
    if (!resourceInfo) return

    try {
      const filename = file ? file.name : ''
      const formData = new FormData()
      formData.append('attachment[mux_upload_id]', uploadId)
      formData.append('attachment[user_filename]', filename)
      formData.append('attachment[video_status]', 'uploading')
      if (file) {
        formData.append('attachment[file_size_in_bytes]', file.size)
        if (file.type) {
          formData.append('attachment[original_file_mime_type]', file.type)
        }
      }
      formData.append(`${resourceInfo.type}_id`, resourceInfo.id)

      const response = await fetch('/admin/attachments', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': this.getCsrfToken()
        }
      })

      if (!response.ok) throw new Error('Failed to create attachment record')

      const data = await response.json()

      // Call append list item action if provided
      if (this.appendListItemActionValue != "" && data.attachments && data.attachments[0]) {
        this.callControllerMethod(this.appendListItemActionValue, data.attachments[0])
      }

      // Reset video upload UI
      if (this.hasVideoStatusTextTarget) {
        this.VideoStatusTextTarget.textContent = 'Video uploaded successfully!'
      }

      setTimeout(() => {
        if (this.hasVideoUploadingContainerTarget) {
          this.VideoUploadingContainerTarget.classList.add('hidden')
        }
        if (this.hasVideoProgressBarTarget) {
          this.VideoProgressBarTarget.style.width = '0%'
        }
        this.FileInputTarget.value = ''
        this.FormTarget.classList.remove('is-uploading')
      }, 2000)

    } catch (error) {
      console.error('Error creating attachment record:', error)
      if (this.hasVideoStatusTextTarget) {
        this.VideoStatusTextTarget.textContent = 'Failed to save video. Please try again.'
      }
    }
  }

}
